/** Web Mercator meters-per-pixel at zoom 0 on the equator. */
export const WEB_MERCATOR_METERS_PER_PIXEL_AT_ZOOM_0 = 156543.03392

const DEG_TO_RAD = Math.PI / 180
const EARTH_RADIUS_METERS = 6378137

export const ACCURACY_RING_COLOR = '#3B82F6'
export const ACCURACY_RING_FILL_OPACITY = 0.08
export const ACCURACY_RING_STROKE_OPACITY = 0.3
export const ACCURACY_RING_STROKE_WIDTH = 1

export function positiveAccuracyRadiusMeters(
  meters: number | null | undefined,
): number | null {
  if (typeof meters !== 'number' || !Number.isFinite(meters) || meters <= 0) {
    return null
  }
  return meters
}

/**
 * Google Data-layer symbol for one accuracy ring.
 * `clickable: false` so the dots layer keeps the click.
 */
export function googlePointCloudAccuracySymbol(input: {
  latitude: number
  zoom: number
  meters: number | null | undefined
  circlePath: unknown
}): { clickable: false; visible: false } | {
  clickable: false
  icon: {
    path: unknown
    scale: number
    fillColor: string
    fillOpacity: number
    strokeColor: string
    strokeOpacity: number
    strokeWeight: number
  }
} {
  const scale = metersToPixelRadius(input.latitude, input.zoom, input.meters ?? 0)
  if (scale <= 0) return { clickable: false, visible: false }
  return {
    clickable: false,
    icon: {
      path: input.circlePath,
      scale,
      fillColor: ACCURACY_RING_COLOR,
      fillOpacity: ACCURACY_RING_FILL_OPACITY,
      strokeColor: ACCURACY_RING_COLOR,
      strokeOpacity: ACCURACY_RING_STROKE_OPACITY,
      strokeWeight: ACCURACY_RING_STROKE_WIDTH,
    },
  }
}

/** Screen radius for a geodesic accuracy circle at the given latitude and zoom. */
export function metersToPixelRadius(
  latitude: number,
  zoom: number,
  meters: number,
): number {
  const positive = positiveAccuracyRadiusMeters(meters)
  if (positive == null || !Number.isFinite(latitude) || !Number.isFinite(zoom)) {
    return 0
  }
  const metersPerPixel =
    (WEB_MERCATOR_METERS_PER_PIXEL_AT_ZOOM_0 * Math.cos(latitude * DEG_TO_RAD)) /
    2 ** zoom
  if (!Number.isFinite(metersPerPixel) || metersPerPixel <= 0) return 0
  return positive / metersPerPixel
}

/** Point `meters` due east of lat/lng, for projecting a pixel radius on a map. */
export function destinationEast(
  latitude: number,
  longitude: number,
  meters: number,
): { lat: number; lng: number } {
  const delta = meters / EARTH_RADIUS_METERS
  const latRad = latitude * DEG_TO_RAD
  const lngRad = longitude * DEG_TO_RAD
  const lat2 = Math.asin(
    Math.sin(latRad) * Math.cos(delta) +
      Math.cos(latRad) * Math.sin(delta) * Math.cos(Math.PI / 2),
  )
  const lng2 =
    lngRad +
    Math.atan2(
      Math.sin(Math.PI / 2) * Math.sin(delta) * Math.cos(latRad),
      Math.cos(delta) - Math.sin(latRad) * Math.sin(lat2),
    )
  return { lat: (lat2 * 180) / Math.PI, lng: (lng2 * 180) / Math.PI }
}
